// src\app\app.component.ts

import { Component, inject, OnInit, ViewEncapsulation } from "@angular/core";
import { Router } from "@angular/router";
import { addIcons } from "ionicons";

import { SplashScreen } from "@capacitor/splash-screen";
import { StatusBar } from "@capacitor/status-bar";

import { Storage } from "@ionic/storage-angular";

import { FormsModule } from "@angular/forms";
import {
  AlertController,
  IonApp,
  IonRouterOutlet,
  MenuController,
  Platform,
  ToastController,
} from "@ionic/angular/standalone";

import {
  calendarOutline,
  hammer,
  help,
  informationCircleOutline,
  logIn,
  logOut,
  mapOutline,
  moonOutline,
  peopleOutline,
  trashOutline,
  personAdd,
  add,
  addCircleOutline,
  arrowBackOutline,
  ban,
  addOutline,
  basketOutline,
  basket,
  briefcaseOutline,
  callOutline,
  cart,
  cartOutline,
  cashOutline,
  chatbubblesOutline,
  checkmarkCircle,
  checkmarkCircleOutline,
  checkmarkDoneOutline,
  checkmarkOutline,
  checkmarkSharp,
  chevronDownOutline,
  chevronForwardCircleOutline,
  closeCircle,
  closeCircleOutline,
  closeOutline,
  createOutline,
  eye,
  eyeOff,
  fastFood,
  fastFoodOutline,
  homeOutline,
  listOutline,
  locateOutline,
  locationOutline,
  mail,
  navigate,
  person,
  personOutline,
  powerOutline,
  remove,
  removeCircleOutline,
  search,
  searchOutline,
  send,
  settings,
  star,
  starHalf,
  starOutline,
  ticket,
  trashBin,
  chevronDownCircleOutline,
  bookmarkOutline,
  businessOutline,
  logOutOutline,
  navigateOutline,
  alertCircleOutline,
  timerOutline,
  timeOutline,
  keyOutline,
  helpCircleOutline,
  eyeOutline,
  arrowForwardOutline,
  shieldCheckmarkOutline,
  clipboardOutline,
  headsetOutline,
} from "ionicons/icons";

import { SwUpdate, VersionReadyEvent } from "@angular/service-worker";
import { PwaService } from "./services/pwa/pwa.service";

import { register } from "swiper/element/bundle";
import { filter } from "rxjs";
import { PushService } from "./services/push/push.service";

register();

@Component({
  selector: "app-root",
  templateUrl: "./app.component.html",
  styleUrls: ["./app.component.scss"],
  imports: [IonRouterOutlet, IonApp, FormsModule],
  providers: [MenuController, ToastController],
  encapsulation: ViewEncapsulation.None,
})
export class AppComponent implements OnInit {
  private router = inject(Router);
  private storage = inject(Storage);
  private swUpdate = inject(SwUpdate);
  private menu = inject(MenuController);
  private platform = inject(Platform);
  private push = inject(PushService);

  private storageReadyPromise: Promise<void> | null = null;

  appPages = [
    { title: "Schedule", url: "/app/tabs/schedule", icon: "calendar" },
    { title: "Speakers", url: "/app/tabs/speakers", icon: "people" },
    { title: "Map", url: "/app/tabs/map", icon: "map" },
    { title: "About", url: "/app/tabs/about", icon: "information-circle" },
  ];

  loggedIn = false;
  dark = false;

  constructor(
    private alertCtrl: AlertController,
    public pwaService: PwaService
  ) {
    this.initializeApp();

    addIcons({
      calendarOutline,
      addOutline,
      peopleOutline,
      mapOutline,
      informationCircleOutline,
      help,
      logOut,
      logIn,
      personAdd,
      moonOutline,
      hammer,
      powerOutline,
      fastFoodOutline,
      fastFood,
      trashOutline,
      searchOutline,
      search,
      cartOutline,
      cart,
      personOutline,
      person,
      locateOutline,
      locationOutline,
      settings,
      briefcaseOutline,
      homeOutline,
      remove,
      add,
      listOutline,
      arrowBackOutline,
      ticket,
      checkmarkCircleOutline,
      closeCircleOutline,
      chevronForwardCircleOutline,
      navigate,
      checkmarkOutline,
      alertCircleOutline,
      callOutline,
      starHalf,
      star,
      starOutline,
      mail,
      eye,
      eyeOff,
      createOutline,
      chevronDownOutline,
      basketOutline,
      basket,
      ban,
      checkmarkCircle,
      closeCircle,
      send,
      checkmarkDoneOutline,
      checkmarkSharp,
      cashOutline,
      chatbubblesOutline,
      trashBin,
      closeOutline,
      removeCircleOutline,
      addCircleOutline,
      chevronDownCircleOutline,
      bookmarkOutline,
      businessOutline,
      logOutOutline,
      navigateOutline,
      timerOutline,
      timeOutline,
      keyOutline,
      helpCircleOutline,
      eyeOutline,
      arrowForwardOutline,
      shieldCheckmarkOutline,
      clipboardOutline,
      headsetOutline
    });
  }

  private ensureStorageReady(): Promise<void> {
    if (!this.storageReadyPromise) {
      this.storageReadyPromise = this.storage.create().then(() => void 0);
    }
    return this.storageReadyPromise;
  }

  async ngOnInit() {
    // Ensure Ionic Storage is ready for anyone using it indirectly
    await this.ensureStorageReady();

    this.checkForUpdates();

    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates.subscribe((event) => {
        switch (event.type) {
          case "VERSION_DETECTED":
            console.log(`Downloading new app version: ${event.version.hash}`);
            break;
          case "VERSION_READY":
            console.log(`Current app version: ${event.currentVersion.hash}`);
            console.log(
              `New app version ready for use: ${event.latestVersion.hash}`
            );
            break;
          case "VERSION_INSTALLATION_FAILED":
            console.log(
              `Failed to install app version '${event.version.hash}': ${event.error}`
            );
            break;
        }
      });
    }
  }

  initializeApp() {
    this.platform.ready().then(async () => {
      // ✅ storage first, then any services that may touch auth/storage
      await this.ensureStorageReady();

      if (this.platform.is("hybrid")) {
        StatusBar.hide();
        SplashScreen.hide();
      }
    });
  }

  async checkForUpdates() {
    if (this.swUpdate.isEnabled) {
      this.swUpdate.versionUpdates
        .pipe(
          filter(
            (evt): evt is VersionReadyEvent => evt.type === "VERSION_READY"
          )
        )
        .subscribe(async () => {
          const alert = await this.alertCtrl.create({
            header: "يوجد تحديث جديد للطبيق",
            message:
              "يوجد تحديث جديد للطبيق. هل تود تحديث التطبيق للنسخة الجديدة؟",
            buttons: [
              { text: "أخبرني لاحقا", role: "cancel" },
              {
                text: "تحديث",
                handler: () => document.location.reload(),
              },
            ],
          });
          await alert.present();
        });

      try {
        await this.swUpdate.checkForUpdate();
      } catch (err) {
        console.error("Failed to check for updates:", err);
      }
    }
  }

  openTutorial() {
    this.menu.enable(false);
    this.storage.set("ion_did_tutorial", false);
    this.router.navigateByUrl("/tutorial");
  }

  toggleDarkMode() {
    document.documentElement.classList.toggle("ion-palette-dark", this.dark);
  }
}
